"use client"

import * as React from "react"
import { Link } from "react-router-dom"
import { CheckIcon, ChevronRightIcon } from "lucide-react"

import { useTranslations } from "@/i18n"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { workspaceSetup } from "@ibada/shared-types"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  cn,
} from "@ibada/ui"

const COMPLETED = workspaceSetup.WorkspaceSetupStepStatus.COMPLETED

function ChecklistRow({ step, onSelect }: { step: workspaceSetup.WorkspaceSetupStep; onSelect?: () => void }) {
  const t = useTranslations()
  const completed = step.status === COMPLETED
  const description = !completed && step.descriptionKey ? t(step.descriptionKey as never) : null
  const content = (
    <>
      <span
        className={cn(
          "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full",
          completed ? "bg-primary text-primary-foreground" : "border-2 border-dotted border-muted-foreground/55",
        )}
      >
        {completed ? <CheckIcon aria-hidden className="size-3 stroke-[3]" /> : null}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span
          className={cn(
            "text-sm leading-5",
            completed ? "text-muted-foreground line-through" : "font-medium text-foreground",
          )}
        >
          {t(step.labelKey as never)}
        </span>
        {description ? (
          <span className="text-xs leading-snug text-muted-foreground">{description}</span>
        ) : null}
      </span>
    </>
  )

  if (!completed && step.actionHref) {
    return (
      <Link
        to={step.actionHref}
        onClick={onSelect}
        className="flex min-w-0 items-start gap-2.5 rounded-md px-2 py-2 transition-colors hover:bg-primary/7 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {content}
      </Link>
    )
  }

  return <div className="flex min-w-0 items-start gap-2.5 px-2 py-2">{content}</div>
}

/** The required steps (with the backend's next step first if it is not already listed) and the optional
 * groups that still have something left to do. */
export function setupChecklistSections(progress: workspaceSetup.WorkspaceSetupProgressResponse) {
  const { steps } = progress.required
  const requiredSteps =
    progress.required.nextStep && !steps.some((step) => step.id === progress.required.nextStep?.id)
      ? [progress.required.nextStep, ...steps]
      : steps
  return {
    requiredSteps,
    nextStep: requiredSteps.find((step) => step.status !== COMPLETED) ?? null,
    // Only surface a suggested group (e.g. Finance) while it still has something left to do.
    suggestedGroups: progress.optionalGroups.filter((group) => group.steps.some((step) => step.status !== COMPLETED)),
  }
}

export function WorkspaceSetupStepList({
  progress,
  onSelect,
}: {
  progress: workspaceSetup.WorkspaceSetupProgressResponse
  onSelect?: () => void
}) {
  const t = useTranslations()
  const { requiredSteps, suggestedGroups } = setupChecklistSections(progress)
  return (
    <>
      <div className="px-2.5 pt-2.5 pb-3">
        {requiredSteps.map((step) => (
          <ChecklistRow key={step.id} step={step} onSelect={onSelect} />
        ))}
      </div>

      {suggestedGroups.map((group) => (
        <div key={group.id} className="border-t border-border px-2.5 pt-3 pb-4">
          <div className="px-2">
            <span className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
              {t(group.labelKey as never)}
            </span>
            {group.noteKey ? (
              <p className="mt-1 text-xs leading-snug text-muted-foreground">{t(group.noteKey as never)}</p>
            ) : null}
          </div>
          <div className="mt-1.5">
            {group.steps.map((step) => (
              <ChecklistRow key={step.id} step={step} onSelect={onSelect} />
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

export function ProgressRing({
  completed,
  total,
  className,
  label,
  trackClassName = "stroke-primary/15",
  indicatorClassName = "stroke-primary",
  labelClassName = "text-primary",
}: {
  completed: number
  total: number
  /** Size and label text, e.g. `size-11 text-xs`. */
  className?: string
  /** What the centre reads. Defaults to `completed/total`. */
  label?: React.ReactNode
  /** Stroke colours, so another surface can tint the ring (e.g. amber when a trial has ended). */
  trackClassName?: string
  indicatorClassName?: string
  labelClassName?: string
}) {
  const radius = 13
  const circumference = 2 * Math.PI * radius
  const ratio = total > 0 ? completed / total : 0
  return (
    <span className={cn("relative inline-flex size-11 shrink-0 items-center justify-center text-xs", className)}>
      <svg viewBox="0 0 32 32" className="absolute inset-0 size-full -rotate-90" aria-hidden>
        <circle cx="16" cy="16" r={radius} fill="none" strokeWidth="3" className={trackClassName} />
        <circle
          cx="16"
          cy="16"
          r={radius}
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          className={cn("transition-[stroke-dashoffset] duration-500", indicatorClassName)}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
        />
      </svg>
      <span className={cn("leading-none font-bold tabular-nums", labelClassName)}>
        {label ?? `${completed}/${total}`}
      </span>
    </span>
  )
}

function ProgressBar({ label, percentage }: { label: string; percentage: number }) {
  return (
    <span className="flex items-center gap-2.5 pt-1">
      <span className="shrink-0 text-xs font-semibold text-primary">{label}</span>
      <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
        <span
          className="block h-full rounded-full bg-primary transition-[width] duration-500"
          style={{ width: `${Math.max(0, Math.min(100, percentage))}%` }}
        />
      </span>
    </span>
  )
}

/** The full checklist: a bottom sheet on a phone, a dialog on a larger screen. */
export function WorkspaceSetupSheet({
  progress,
  open,
  onOpenChange,
}: {
  progress: workspaceSetup.WorkspaceSetupProgressResponse
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const tSetup = useTranslations("dashboard.admin.setup")
  const isMobile = useIsMobile()
  const { completedSteps, totalSteps, percentage } = progress.required
  const title = tSetup("title")
  const progressBar = (
    <ProgressBar label={tSetup("doneCount", { completed: completedSteps, total: totalSteps })} percentage={percentage} />
  )
  const steps = <WorkspaceSetupStepList progress={progress} onSelect={() => onOpenChange(false)} />

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader className="group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription asChild>
              <div>{progressBar}</div>
            </DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto overscroll-contain pb-[max(1rem,env(safe-area-inset-bottom))]">{steps}</div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" className="flex max-h-[85dvh] flex-col" onOpenAutoFocus={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div>{progressBar}</div>
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto pb-2">{steps}</div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The setup checklist as one row at the top of the home page, opening {@link WorkspaceSetupSheet}, so it
 * never covers the page the way a floating card did.
 */
export function WorkspaceSetupWidget({ progress }: { progress: workspaceSetup.WorkspaceSetupProgressResponse }) {
  const t = useTranslations()
  const tSetup = useTranslations("dashboard.admin.setup")
  const [open, setOpen] = React.useState(false)

  // `visible` is the SINGLE authority on whether this card shows. The backend already folds "required steps
  // done" into it — `visible = !isComplete || finance-still-pending` — deliberately keeping the card up to
  // nudge the optional finance setup once the basics are done.
  if (!progress.visible) {
    return null
  }

  const { completedSteps, totalSteps } = progress.required
  const { nextStep } = setupChecklistSections(progress)

  return (
    <>
      <button
        type="button"
        data-testid="workspace-setup-widget"
        onClick={() => setOpen(true)}
        className="flex w-full min-w-0 items-center gap-3 rounded-2xl border border-primary/20 bg-primary/5 px-3 py-2.5 text-left transition-colors hover:bg-primary/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ProgressRing completed={completedSteps} total={totalSteps} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-foreground">{tSetup("title")}</span>
          {nextStep ? (
            <span className="block truncate text-xs text-muted-foreground">
              {tSetup("nextStepLabel")} : {t(nextStep.labelKey as never)}
            </span>
          ) : null}
        </span>
        <ChevronRightIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      </button>
      <WorkspaceSetupSheet progress={progress} open={open} onOpenChange={setOpen} />
    </>
  )
}
